import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiHelper } from "../../../utils/apiHelper";
import { toast } from "react-toastify";

export const fetchMarketInfo = createAsyncThunk(
    "market/fetchMarketInfo",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/market`);
            if (response.data.code === "00000") {
                toast.success('Pazar bilgileri başarıyla yüklendi');
                return response.data.marketInfo;
            }
            throw new Error("Failed to fetch market info");
        } catch (error) {
            toast.error('Pazar bilgileri yüklenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const updateMarketInfo = createAsyncThunk(
    "market/updateMarketInfo",
    async ({ startupId, marketData }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put(`/startup/${startupId}/market`, marketData);
            if (response.data.code === "00000") {
                toast.success('Pazar bilgileri başarıyla güncellendi');
                return marketData;
            }
            throw new Error("Failed to update market info");
        } catch (error) {
            toast.error('Pazar bilgileri güncellenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const addCompetitor = createAsyncThunk(
    "market/addCompetitor",
    async ({ startupId, competitorData }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post(`/startup/${startupId}/competitor`, competitorData);
            if (response.data.code === "00000") {
                toast.success('Rakip bilgisi başarıyla eklendi');
                return response.data.competitor;
            }
            throw new Error("Failed to add competitor");
        } catch (error) {
            toast.error('Rakip bilgisi eklenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const deleteCompetitor = createAsyncThunk(
    "market/deleteCompetitor",
    async ({ startupId, competitorId }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete(`/startup/${startupId}/competitor/${competitorId}`);
            if (response.data.code === "00000") {
                toast.success('Rakip bilgisi başarıyla silindi');
                return competitorId;
            }
            throw new Error("Failed to delete competitor");
        } catch (error) {
            toast.error('Rakip bilgisi silinirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

// Market Competition actions
export const fetchMarketCompetition = createAsyncThunk(
    "market/fetchMarketCompetition",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/market-competition`);
            if (response.data.code === "00000") {
                console.log("response.data.marketCompetition", response.data.marketCompetition);
                return response.data.marketCompetition;
            } else {
                throw new Error("Failed to fetch market competition");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateMarketCompetition = createAsyncThunk(
    "market/updateMarketCompetition",
    async (data, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/market-competition", data);
            if (response.data.code === "00000") {
                toast.success("Pazar rekabet bilgileri başarıyla güncellendi");
                return response.data.marketCompetition;
            } else {
                throw new Error("Failed to update market competition");
            }
        } catch (error) {
            toast.error("Pazar rekabet bilgileri güncellenirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Market Other Topics actions
export const fetchMarketOtherTopics = createAsyncThunk(
    "market/fetchMarketOtherTopics",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/market-other-topics`);
            if (response.data.code === "00000") {
                return response.data.otherTopics;
            } else {
                throw new Error("Failed to fetch market other topics");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createMarketOtherTopic = createAsyncThunk(
    "market/createMarketOtherTopic",
    async (data, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/market-other-topic", data);
            if (response.data.code === "00000") {
                toast.success("Diğer konu başarıyla eklendi");
                return response.data;
            } else {
                throw new Error("Failed to create market other topic");
            }
        } catch (error) {
            toast.error("Diğer konu eklenirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deleteMarketOtherTopic = createAsyncThunk(
    "market/deleteMarketOtherTopic",
    async (additionalTopicId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/market-other-topic', {
                data: { additionalTopicId }
            });
            if (response.data.code === "00000") {
                toast.success("Diğer konu başarıyla silindi");
                return additionalTopicId;
            } else {
                throw new Error("Failed to delete market other topic");
            }
        } catch (error) {
            toast.error("Diğer konu silinirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

const marketSlice = createSlice({
    name: "market",
    initialState: {
        marketInfo: null,
        marketCompetition: null,
        competitors: [],
        otherTopics: [],
        loading: false,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchMarketInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMarketInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.marketInfo = action.payload;
            })
            .addCase(fetchMarketInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateMarketInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMarketInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.marketInfo = action.payload;
            })
            .addCase(updateMarketInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(addCompetitor.fulfilled, (state, action) => {
                state.competitors.push(action.payload);
            })
            .addCase(deleteCompetitor.fulfilled, (state, action) => {
                state.competitors = state.competitors.filter(comp => comp.id !== action.payload);
            })
            .addCase(fetchMarketOtherTopics.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMarketOtherTopics.fulfilled, (state, action) => {
                state.loading = false;
                state.otherTopics = action.payload;
            })
            .addCase(fetchMarketOtherTopics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createMarketOtherTopic.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createMarketOtherTopic.fulfilled, (state, action) => {
                state.loading = false;
                state.otherTopics.push(action.payload);
            })
            .addCase(createMarketOtherTopic.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deleteMarketOtherTopic.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteMarketOtherTopic.fulfilled, (state, action) => {
                state.loading = false;
                state.otherTopics = state.otherTopics.filter(
                    topic => topic.additionalTopicId !== action.payload
                );
            })
            .addCase(deleteMarketOtherTopic.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchMarketCompetition.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMarketCompetition.fulfilled, (state, action) => {
                state.loading = false;
                state.marketCompetition = action.payload;
                state.error = null;
            })
            .addCase(fetchMarketCompetition.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateMarketCompetition.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMarketCompetition.fulfilled, (state, action) => {
                state.loading = false;
                state.marketCompetition = action.payload;
                state.error = null;
            })
            .addCase(updateMarketCompetition.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export default marketSlice.reducer; 